import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // VE — une seule ligne ouverte à la fois. On referme les autres AVANT que
    // le navigateur n'ouvre celle-ci. Uniquement au clic : `#setDefaultOpenState`
    // rejoue l'état par défaut à chaque changement de breakpoint et provoquerait
    // des fermetures fantômes au redimensionnement.
    if (!this.details.open) this.#closeSiblings();
  };

  /**
   * VE — Closes the other rows of the same accordion group.
   *
   * Volontairement limité au conteneur `.accordion` (blocks/accordion.liquid) :
   * les autres usages de <accordion-custom> — filtres de collection, tri, menu,
   * résumé du panier — n'ont pas cette classe et conservent l'ouverture
   * multiple. Refermer un filtre parce qu'on en ouvre un autre serait hostile.
   */
  #closeSiblings() {
    const group = this.closest('.accordion');

    if (!group) return;

    for (const sibling of group.querySelectorAll('accordion-custom')) {
      // Un accordéon imbriqué dans le contenu d'une ligne appartient à son
      // propre groupe : on ne le referme pas depuis le groupe parent.
      if (sibling === this || sibling.closest('.accordion') !== group) continue;

      const details = sibling.querySelector('details');

      if (details instanceof HTMLDetailsElement) details.open = false;
    }
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
